'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { api } from '@/lib/utils/api';

export interface ForecastDay {
	day: number;
	/** Giorno civile Europe/Rome, 'YYYY-MM-DD'. */
	date: string | null;
	production: number;
	consumption: number;
	sharedEnergy: number;
	confidence: number;
}

/** Curva oraria di consumo di un giorno previsionale (INB-35). */
export interface ConsumptionForecastDay {
	date: string;
	dayType: 'feriale' | 'festivo';
	totalKwh: number;
	/** 24 valori in kWh, indice = ora civile Europe/Rome. */
	hours: number[];
	peakHour: number;
	peakKwh: number;
}

export interface ConsumptionForecast {
	available: boolean;
	/** POD di prelievo che compongono la previsione. */
	pods: number;
	annualKwh: number;
	days: ConsumptionForecastDay[];
	totalKwh: number;
	/** Profili reali dei soci oppure stima dalla configurazione. */
	source: 'member-profiles' | 'config-estimate';
}

/** Curva oraria di produzione FV di un giorno previsionale (INB-39). */
export interface ProductionForecastDay {
	date: string;
	totalKwh: number;
	/** 24 valori in kWh, indice = ora civile Europe/Rome. */
	hours: number[];
	peakHour: number;
	peakKwh: number;
	/** `physics` = irraggiamento orario reale; `pvgis-baseline` = resa mensile. */
	source: 'physics' | 'pvgis-baseline';
}

export interface ProductionForecast {
	available: boolean;
	/** POD di immissione che compongono la previsione. */
	pods: number;
	kwp: number;
	days: ProductionForecastDay[];
	totalKwh: number;
	/** Giorno di massima produzione nella finestra. */
	peakDate: string | null;
}

interface UseForecastReturn {
	forecast: ForecastDay[];
	consumption: ConsumptionForecast | null;
	production: ProductionForecast | null;
	source: 'real-data' | 'config-estimate' | 'no-data';
	isLoading: boolean;
	error: string | null;
}

interface ForecastParams {
	/** Org (configurazione) di cui calcolare la previsione — obbligatorio. */
	orgId?: string | null;
	realProd?: number;
	realCons?: number;
	realShared?: number;
	kwp?: number;
	cons?: number;
}

/**
 * Cadenza di rinfresco, allineata al giro dei 15 minuti che riempie la cache
 * meteo (INB-36): la previsione si aggiorna insieme ai dati su cui poggia.
 * Prima il forecast si ricaricava solo al montaggio e sugli eventi realtime,
 * quindi una dashboard lasciata aperta mostrava la previsione dell'ora prima.
 */
const POLL_MS = 15 * 60 * 1000;

const EMPTY_FORECAST: ForecastDay[] = [];

interface ForecastPayload {
	forecast: ForecastDay[];
	source: string;
	consumption?: ConsumptionForecast;
	production?: ProductionForecast;
}

const forecastKey = (p: ForecastParams) =>
	[
		'forecasting',
		p.orgId ?? null,
		p.realProd ?? null,
		p.realCons ?? null,
		p.realShared ?? null,
		p.kwp ?? null,
		p.cons ?? null,
	] as const;

/**
 * ADR-006: backed by React Query — cached e deduplicato tra pannelli e
 * navigazioni. Conserva il `refetchInterval` di 15 minuti (allineato al giro
 * meteo, INB-36) e l'invalidazione su `energy_readings`.
 */
export function useForecastData(params: ForecastParams): UseForecastReturn {
	const queryClient = useQueryClient();

	const { data, isLoading, error } = useQuery({
		queryKey: forecastKey(params),
		enabled: !!params.orgId,
		refetchInterval: POLL_MS,
		queryFn: () => {
			const query: Record<string, string | number | undefined> = { orgId: params.orgId as string };
			if (params.realProd) query.realProd = params.realProd;
			if (params.realCons) query.realCons = params.realCons;
			if (params.realShared) query.realShared = params.realShared;
			if (params.kwp) query.kwp = params.kwp;
			if (params.cons) query.cons = params.cons;
			return api.get<ForecastPayload>('/api/forecasting', query);
		},
	});

	// Supabase Realtime — aggiorna il forecast quando arrivano nuove letture energetiche.
	useEffect(() => {
		return subscribeToTable(
			'forecast-realtime',
			{ event: '*', schema: 'public', table: 'energy_readings' },
			() => {
				queryClient.invalidateQueries({ queryKey: ['forecasting'] });
			},
		);
	}, [queryClient]);

	return {
		forecast: data?.forecast ?? EMPTY_FORECAST,
		consumption: data?.consumption?.available ? data.consumption : null,
		production: data?.production?.available ? data.production : null,
		source: (data?.source as UseForecastReturn['source']) ?? 'no-data',
		isLoading: params.orgId ? isLoading : true,
		error: error ? (error instanceof Error ? error.message : 'Errore forecast') : null,
	};
}
